import { useState, useEffect, useRef } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Fuel, 
  Plus, 
  Search, 
  Calendar,
  Euro,
  Car,
  FileImage,
  Trash2,
  Edit,
  X,
  Upload,
  Download,
  Eye,
  Loader2,
  TrendingUp,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';


export default function Petrol() {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState(null);
  const [filterDriver, setFilterDriver] = useState('all');
  const [filterVehicle, setFilterVehicle] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const fileInputRef = useRef(null);
  
  // Month/Year filter with navigation
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const monthsList = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  
  const yearsList = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    yearsList.push(y);
  }
  
  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };
  
  const [formData, setFormData] = useState({
    driver_id: '',
    vehicle_id: '',
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    kilometers: '',
    notes: '',
    receipt_data: null,
    receipt_filename: null
  });

  useEffect(() => {
    fetchEntries();
    fetchDrivers();
    fetchVehicles();
    fetchStats();
  }, [filterDriver, filterVehicle, selectedMonth, selectedYear]);

  const fetchEntries = async () => {
    try {
      // Calculate start and end dates for selected month
      const monthStart = new Date(selectedYear, selectedMonth - 1, 1);
      const monthEnd = new Date(selectedYear, selectedMonth, 0); // Last day of month
      
      let params = [
        `start_date=${monthStart.toISOString().slice(0, 10)}`,
        `end_date=${monthEnd.toISOString().slice(0, 10)}`
      ];
      if (filterDriver !== 'all') params.push(`driver_id=${filterDriver}`);
      if (filterVehicle !== 'all') params.push(`vehicle_id=${filterVehicle}`);
      const queryString = `?${params.join('&')}`;
      const response = await axios.get(`${API}/petrol${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching petrol entries:', error);
      toast.error('Failed to load petrol entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/users?role=driver`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const params = filterDriver !== 'all' ? `?driver_id=${filterDriver}` : '';
      const response = await axios.get(`${API}/petrol/summary/stats${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          receipt_data: reader.result,
          receipt_filename: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      driver_id: '',
      vehicle_id: '',
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      kilometers: '',
      notes: '',
      receipt_data: null,
      receipt_filename: null
    });
    setEditingEntry(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    try {
      const payload = {
        driver_id: formData.driver_id || null,
        vehicle_id: formData.vehicle_id || null,
        date: formData.date ? new Date(formData.date).toISOString() : new Date().toISOString(),
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        kilometers: formData.kilometers ? parseFloat(formData.kilometers) : 0,
        notes: formData.notes || null,
        receipt_data: formData.receipt_data,
        receipt_filename: formData.receipt_filename
      };

      if (editingEntry) {
        await axios.put(`${API}/petrol/${editingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Petrol entry updated');
      } else {
        await axios.post(`${API}/petrol`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Petrol entry added');
      }
      
      setShowModal(false);
      resetForm();
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error saving petrol entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (entry) => {
    let receiptData = entry.receipt_data || null;
    if (!receiptData && (entry.has_receipt || entry.receipt_filename)) {
      try {
        const res = await axios.get(`${API}/petrol/${entry.id}/receipt`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        receiptData = res.data?.receipt_data || null;
      } catch (err) {
        console.error('Fetch receipt for edit failed:', err);
      }
    }
    setEditingEntry(entry);
    setFormData({
      driver_id: entry.driver_id,
      vehicle_id: entry.vehicle_id || '',
      date: entry.date ? entry.date.slice(0, 10) : '',
      amount: entry.amount?.toString() || '',
      kilometers: entry.kilometers?.toString() || '',
      notes: entry.notes || '',
      receipt_data: receiptData,
      receipt_filename: entry.receipt_filename
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axios.delete(`${API}/petrol/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const viewReceipt = async (entry) => {
    if (entry.receipt_data) {
      setSelectedReceipt({ data: entry.receipt_data, filename: entry.receipt_filename });
      setShowReceiptModal(true);
      return;
    }
    if (!entry.has_receipt && !entry.receipt_filename) return;
    try {
      const res = await axios.get(`${API}/petrol/${entry.id}/receipt`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.receipt_data) {
        setSelectedReceipt({ data: res.data.receipt_data, filename: res.data.receipt_filename || entry.receipt_filename });
        setShowReceiptModal(true);
      } else {
        toast.error('Receipt not available');
      }
    } catch (err) {
      console.error('Fetch receipt error:', err);
      toast.error('Failed to load receipt');
    }
  };

  const downloadReceiptFor = async (entry) => {
    let fileData = entry.receipt_data;
    let fileName = entry.receipt_filename || 'receipt';
    if (!fileData && (entry.has_receipt || entry.receipt_filename)) {
      try {
        const res = await axios.get(`${API}/petrol/${entry.id}/receipt`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fileData = res.data?.receipt_data;
        fileName = res.data?.receipt_filename || fileName;
      } catch (err) {
        console.error('Fetch receipt for download failed:', err);
        toast.error('Failed to download receipt');
        return;
      }
    }
    if (fileData) downloadFile(fileData, fileName);
  };

  const downloadFile = (fileData, fileName) => {
    if (!fileData) return;
    
    const link = document.createElement('a');
    link.href = fileData;
    link.download = fileName || 'receipt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Petrol Management</h1>
          <p className="text-slate-600 mt-1">Track fuel expenses and consumption</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="add-petrol-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Petrol Entry
        </Button>
      </div>

      {/* Month/Year Navigation - Move before stats */}
      <div className="bg-slate-100 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={goToPreviousMonth}
            className="h-12 w-12 bg-white rounded-lg border-slate-200"
            data-testid="petrol-prev-month-btn"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </Button>
          
          <Select 
            value={selectedMonth.toString()} 
            onValueChange={(v) => setSelectedMonth(parseInt(v))}
          >
            <SelectTrigger className="w-[140px] h-12 bg-white rounded-lg border-slate-200" data-testid="petrol-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthsList.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select 
            value={selectedYear.toString()} 
            onValueChange={(v) => setSelectedYear(parseInt(v))}
          >
            <SelectTrigger className="w-[100px] h-12 bg-white rounded-lg border-slate-200" data-testid="petrol-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearsList.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="icon"
            onClick={goToNextMonth}
            className="h-12 w-12 bg-white rounded-lg border-slate-200"
            data-testid="petrol-next-month-btn"
          >
            <ChevronRight className="h-5 w-5 text-slate-600" />
          </Button>
        </div>
        <p className="text-center text-sm text-slate-600 mt-2">
          Showing data for: <span className="font-semibold">{monthsList.find(m => m.value === selectedMonth)?.label} {selectedYear}</span>
        </p>
      </div>

      {/* Monthly Stats Cards - Calculated from filtered entries */}
      {(() => {
        const totalSpent = entries.reduce((sum, e) => sum + (e.amount || 0), 0);
        const totalEntries = entries.length;
        const totalKm = entries.reduce((sum, e) => sum + (e.kilometers || 0), 0);
        const costPerKm = totalKm > 0 ? totalSpent / totalKm : 0;
        
        return (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Euro className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Spent</p>
                  <p className="text-xl font-bold text-slate-900">€{totalSpent.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Fuel className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Entries</p>
                  <p className="text-xl font-bold text-slate-900">{totalEntries}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Car className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">KM Tracked</p>
                  <p className="text-xl font-bold text-slate-900">{totalKm.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 3})} km</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Cost per KM</p>
                  <p className="text-xl font-bold text-slate-900">€{costPerKm.toFixed(3)}</p>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-slate-400" />
            <span className="font-medium text-slate-700">Additional Filters</span>
            {(filterDriver !== 'all' || filterVehicle !== 'all') && (
              <Badge className="ml-2 bg-blue-500 text-white">Active</Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterDriver('all');
              setFilterVehicle('all');
            }}
          >
            Clear Filters
          </Button>
        </div>
        
        <div className="flex items-center gap-4 flex-wrap">
          {/* Driver Filter */}
          <div className="w-48">
            <Label className="text-xs text-slate-500 mb-1 block">Driver</Label>
            <Select value={filterDriver} onValueChange={setFilterDriver}>
              <SelectTrigger data-testid="filter-driver-select">
                <SelectValue placeholder="All Drivers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                {drivers.map(driver => (
                  <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Vehicle Filter */}
          <div className="w-48">
            <Label className="text-xs text-slate-500 mb-1 block">Vehicle</Label>
            <Select value={filterVehicle} onValueChange={setFilterVehicle}>
              <SelectTrigger data-testid="filter-vehicle-select">
                <SelectValue placeholder="All Vehicles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vehicles</SelectItem>
                {vehicles.map(vehicle => (
                  <SelectItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.license_plate || vehicle.plate_number} {vehicle.make ? `(${vehicle.make})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Period Summary */}
      <div className="mb-4 text-sm text-slate-600 text-center">
        Showing petrol entries for <strong>{monthsList.find(m => m.value === selectedMonth)?.label} {selectedYear}</strong>
        {filterDriver !== 'all' && ` • Driver: ${drivers.find(d => d.id === filterDriver)?.name || 'Selected'}`}
        {filterVehicle !== 'all' && ` • Vehicle: ${vehicles.find(v => v.id === filterVehicle)?.plate_number || vehicles.find(v => v.id === filterVehicle)?.license_plate || 'Selected'}`}
        {` • ${entries.length} entries`}
      </div>

      {/* Entries Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Date</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Driver</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Vehicle</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Amount</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Kilometers</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Receipt</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Notes</th>
              <th className="text-right px-6 py-3 text-sm font-semibold text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" />
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                  No petrol entries found
                </td>
              </tr>
            ) : entries.map(entry => (
              <tr key={entry.id} className="hover:bg-slate-50" data-testid={`petrol-row-${entry.id}`}>
                <td className="px-6 py-4 text-sm text-slate-900">{formatDate(entry.date)}</td>
                <td className="px-6 py-4 text-sm text-slate-900">{entry.driver_name || 'Unknown'}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{entry.vehicle_plate || '-'}</td>
                <td className="px-6 py-4 text-sm font-medium text-slate-900">€{entry.amount?.toFixed(2)}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{entry.kilometers?.toLocaleString()} km</td>
                <td className="px-6 py-4">
                  {(entry.has_receipt || entry.receipt_data || entry.receipt_filename) ? (
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => viewReceipt(entry)}
                        className="text-blue-600 hover:text-blue-700"
                        title="View receipt"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => downloadReceiptFor(entry)}
                        className="text-emerald-600 hover:text-emerald-700"
                        title="Download receipt"
                        data-testid={`download-petrol-${entry.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-slate-400 text-sm">No receipt</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">{entry.notes || '-'}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Petrol Entry' : 'Add Petrol Entry'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Driver</Label>
              <Select 
                value={formData.driver_id} 
                onValueChange={(value) => setFormData({ ...formData, driver_id: value })}
              >
                <SelectTrigger className="mt-1" data-testid="petrol-driver-select">
                  <SelectValue placeholder="Select driver (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Vehicle</Label>
              <Select 
                value={formData.vehicle_id} 
                onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
              >
                <SelectTrigger className="mt-1" data-testid="petrol-vehicle-select">
                  <SelectValue placeholder="Select vehicle (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map(vehicle => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>{vehicle.plate_number} {vehicle.model && `- ${vehicle.model}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Kilometers</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={formData.kilometers}
                  onChange={(e) => setFormData({ ...formData, kilometers: e.target.value })}
                  placeholder="Current km"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Receipt (Optional)</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="flex-1"
                />
              </div>
              {formData.receipt_filename && (
                <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                  <FileImage className="h-4 w-4" />
                  {formData.receipt_filename}
                </p>
              )}
            </div>
            
            <div>
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes..."
                className="mt-1"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingEntry ? 'Update' : 'Add Entry'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Receipt</span>
              {selectedReceipt && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadFile(selectedReceipt.data, selectedReceipt.filename || 'receipt')}
                  className="flex items-center gap-1"
                  data-testid="download-modal-petrol-receipt"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedReceipt && (
            <div className="flex flex-col items-center gap-4">
              <img 
                src={selectedReceipt.data} 
                alt="Receipt" 
                className="max-h-[70vh] object-contain rounded-lg"
              />
              {selectedReceipt.filename && (
                <p className="text-sm text-slate-500">{selectedReceipt.filename}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
